import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { SubmissionPermissionType } from '../../../../models/permissions/dto/submission-permission-type';
import { InternalUserSubmissionTypeFormGroup } from '../../../settings/components/internal-users/view-internal-user/forms/internal-user-submission-type-form-group';

import { FormArray } from '@angular/forms';
import { SubmissionPermissionSelectionTypeEnum } from 'src/app/models/permissions/enum/submission-permission-selection-type.enum';
import { InternalUserSubmissionPermissionFormGroup } from '../../../settings/components/internal-users/view-internal-user/forms/internal-user-submission-permission-form-group';
import { SubmissionPermissionTypeEnum } from '../../../../models/permissions/enum/submission-permission-type.enum';
import { CheckboxState } from '../indeterminate-checkbox/indeterminate-checkbox.component';
import { InternalUserSubmissionPermissionFormStateUtils } from '../../../settings/components/internal-users/view-internal-user/utilities/internal-user-submission-permission-form-state-utils';
import { SubmissionPermissionAudience } from '../../../../pipes/submission-permission-tooltip.pipe';
import { TrackByUtils } from '../../../../utils/track-by-utils';

@Component({
  selector: 'app-submission-permission-checkbox-table',
  templateUrl: './submission-permission-checkbox-table.component.html',
  styleUrls: ['./submission-permission-checkbox-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class SubmissionPermissionCheckboxTableComponent {
  @Input() submissionPermissionTypes: SubmissionPermissionType[] = [];
  @Input({ required: true }) formArray!: FormArray<InternalUserSubmissionTypeFormGroup>;
  @Input() audience: SubmissionPermissionAudience = 'member';

  public readonly permanentlyDisabledIds = InternalUserSubmissionPermissionFormStateUtils.PERMANENTLY_DISABLED_IDS;
  public readonly trackById = TrackByUtils.trackById;

  public selectAllOfTypeState(type: SubmissionPermissionTypeEnum): CheckboxState {
    const state = this.determineSelectAllState(type);
    if (state === SubmissionPermissionSelectionTypeEnum.All) return 'checked';
    if (state === SubmissionPermissionSelectionTypeEnum.Some) return 'indeterminate';
    return 'unchecked';
  }

  public onSelectAllOfTypeClicked(permissionTypeId: number): void {
    const status = this.determineSelectAllState(permissionTypeId);
    if (status === SubmissionPermissionSelectionTypeEnum.All) {
      this.deselectAllOfType(permissionTypeId);
    } else {
      this.selectAllOfType(permissionTypeId);
    }
  }

  private selectAllOfType(permissionTypeId: SubmissionPermissionTypeEnum): void {
    this.formArray.controls.forEach(typeFormGroup => {
      const permissionFormGroups = typeFormGroup.controls.submissionPermissions;
      permissionFormGroups.controls.forEach(permissionFormGroup => {
        if (
          InternalUserSubmissionPermissionFormStateUtils.isPermanentlyDisabled(permissionFormGroup.controls.id.value)
        ) {
          return;
        }
        if (
          permissionFormGroup.controls.permissionTypeId.value === permissionTypeId &&
          !permissionFormGroup.controls.hasPermission.value
        ) {
          this.setPermission(permissionFormGroup, typeFormGroup);
        }
      });
    });
  }

  private deselectAllOfType(permissionTypeId: SubmissionPermissionTypeEnum): void {
    this.formArray.controls.forEach(typeFormGroup => {
      const permissionFormGroups = typeFormGroup.controls.submissionPermissions;
      permissionFormGroups.controls.forEach(permissionFormGroup => {
        if (
          InternalUserSubmissionPermissionFormStateUtils.isPermanentlyDisabled(permissionFormGroup.controls.id.value)
        ) {
          return;
        }
        if (
          permissionFormGroup.controls.permissionTypeId.value === permissionTypeId &&
          permissionFormGroup.controls.hasPermission.value
        ) {
          this.setPermission(permissionFormGroup, typeFormGroup);
        }
      });
    });
  }

  public onPermissionChange(
    permissionFormGroup: InternalUserSubmissionPermissionFormGroup,
    typeFormGroup: InternalUserSubmissionTypeFormGroup
  ): void {
    const hasPermission = permissionFormGroup.controls.hasPermission.value;
    if (hasPermission) {
      this.selectRelatedPermissions(permissionFormGroup.controls.permissionTypeId.value, typeFormGroup);
    } else {
      this.deselectRelatedPermissions(permissionFormGroup.controls.permissionTypeId.value, typeFormGroup);
    }
    this.applyIdDependencies(permissionFormGroup.controls.id.value, hasPermission, typeFormGroup);
  }

  public setPermission(
    permissionFormGroup: InternalUserSubmissionPermissionFormGroup,
    typeFormGroup: InternalUserSubmissionTypeFormGroup
  ): void {
    if (InternalUserSubmissionPermissionFormStateUtils.isPermanentlyDisabled(permissionFormGroup.controls.id.value)) {
      return;
    }
    const hasPermissionControl = permissionFormGroup.controls.hasPermission;
    hasPermissionControl.setValue(!hasPermissionControl.value);

    if (hasPermissionControl.value) {
      this.selectRelatedPermissions(permissionFormGroup.controls.permissionTypeId.value, typeFormGroup);
    } else {
      this.deselectRelatedPermissions(permissionFormGroup.controls.permissionTypeId.value, typeFormGroup);
    }
    this.applyIdDependencies(permissionFormGroup.controls.id.value, hasPermissionControl.value, typeFormGroup);
  }

  private applyIdDependencies(
    permissionId: number,
    hasPermission: boolean,
    typeFormGroup: InternalUserSubmissionTypeFormGroup
  ): void {
    const perms = typeFormGroup.controls.submissionPermissions.controls;
    const deps = InternalUserSubmissionPermissionFormStateUtils.ID_DEPENDENCIES;

    if (hasPermission) {
      const requiredIds = deps.filter(d => d.id === permissionId).map(d => d.requiresId);
      perms
        .filter(p => requiredIds.includes(p.controls.id.value) && !p.controls.hasPermission.value)
        .forEach(p => p.patchValue({ hasPermission: true }));
    } else {
      const dependentIds = deps.filter(d => d.requiresId === permissionId).map(d => d.id);
      perms
        .filter(p => dependentIds.includes(p.controls.id.value) && p.controls.hasPermission.value)
        .forEach(p => p.patchValue({ hasPermission: false }));
    }
  }

  private selectRelatedPermissions(
    permissionActionType: number,
    relatedPermissionFormGroup: InternalUserSubmissionTypeFormGroup
  ): void {
    let dependentControls: InternalUserSubmissionPermissionFormGroup[] = [];
    switch (permissionActionType) {
      case SubmissionPermissionTypeEnum.Process:
      case SubmissionPermissionTypeEnum.Approve:
      case SubmissionPermissionTypeEnum.Override:
      case SubmissionPermissionTypeEnum.Create:
        dependentControls = relatedPermissionFormGroup.controls.submissionPermissions.controls.filter(
          perm => perm.controls.permissionTypeId.value === SubmissionPermissionTypeEnum.View
        );
        break;
      case SubmissionPermissionTypeEnum.ApproveOwn:
        dependentControls = relatedPermissionFormGroup.controls.submissionPermissions.controls.filter(perm =>
          [SubmissionPermissionTypeEnum.View, SubmissionPermissionTypeEnum.Process].includes(
            perm.controls.permissionTypeId.value
          )
        );
        break;
    }
    dependentControls.forEach(control => control.patchValue({ hasPermission: true }));
  }

  private deselectRelatedPermissions(
    permissionActionType: number,
    relatedPermissionFormGroup: InternalUserSubmissionTypeFormGroup
  ): void {
    let dependentControls: InternalUserSubmissionPermissionFormGroup[] = [];
    switch (permissionActionType) {
      case SubmissionPermissionTypeEnum.View:
        dependentControls = relatedPermissionFormGroup.controls.submissionPermissions.controls.filter(
          perm => perm.controls.permissionTypeId.value !== SubmissionPermissionTypeEnum.View
        );
        break;
      case SubmissionPermissionTypeEnum.Process:
        dependentControls = relatedPermissionFormGroup.controls.submissionPermissions.controls.filter(
          perm => perm.controls.permissionTypeId.value === SubmissionPermissionTypeEnum.ApproveOwn
        );
        break;
    }
    dependentControls.forEach(control => control.patchValue({ hasPermission: false }));
  }

  private determineSelectAllState(targetPermissionTypeId: number): SubmissionPermissionSelectionTypeEnum {
    let countWithPermission = 0;
    let totalMatchingPermissions = 0;

    this.formArray.controls.forEach(submissionType => {
      const permissions = submissionType.controls.submissionPermissions;

      permissions.controls.forEach(permission => {
        if (permission.controls.permissionTypeId.value === targetPermissionTypeId) {
          if (InternalUserSubmissionPermissionFormStateUtils.isPermanentlyDisabled(permission.controls.id.value)) {
            return;
          }
          totalMatchingPermissions++;
          if (permission.controls.hasPermission.value) {
            countWithPermission++;
          }
        }
      });
    });

    if (countWithPermission === totalMatchingPermissions) {
      return SubmissionPermissionSelectionTypeEnum.All;
    } else if (countWithPermission > 0) {
      return SubmissionPermissionSelectionTypeEnum.Some;
    } else {
      return SubmissionPermissionSelectionTypeEnum.None;
    }
  }
}
